import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { FlashBanner } from '@/components/FlashBanner'
import { ErrorBanner } from '@/components/ErrorBanner'
import { ScreenGuideDialog } from '@/components/ScreenGuideDialog'
import { Button } from '@/components/ui/button'
import { useScreenGuide } from '@/hooks/useScreenGuide'
import { usePollUntil } from '@/hooks/usePolling'
import type { ProjectNav } from '@/types/project'
import type { RequirementsSpecificationSummary } from '@/types/requirements_specification'
import type { RiskData } from '@/types/risk_score'
import type { SharedData } from '@/types/shared'
import { ScoreArc } from './components/ScoreArc'
import { StatBar } from './components/StatBar'
import { CATEGORY_ITEMS } from './components/CategorySection'
import { VOLUME_ITEMS, RISK_LEVEL_META } from './components/scoreItems'
import { RisksTab } from './components/RisksTab'
import { onboardingContent } from './onboardingContent'

interface Props {
  project_nav: ProjectNav
  requirements_specification_summary: RequirementsSpecificationSummary
  requirements_quality: RiskData | null
  kg_stale: boolean
  audit_in_progress: boolean
}

// ===== メインページ =====

export default function RequirementsQualityShow ({
  project_nav: project,
  requirements_specification_summary: _spec,
  requirements_quality: requirementsQuality,
  kg_stale: kgStale,
  audit_in_progress: auditInProgress
}: Props): React.ReactElement {
  const { props } = usePage<SharedData>()
  const publicFunnelEnabled = props.public_funnel_enabled
  const guide = useScreenGuide(
    'requirements_quality',
    props.current_user?.onboardings ?? {},
    props.current_user?.account_type === 'demo'
  )
  const handleClose = guide.close
  const handleOpenHelp = guide.openHelp

  const [rechecking, setRechecking] = useState(false)
  const [pollTimedOut, setPollTimedOut] = useState(false)
  usePollUntil(auditInProgress, 5000, { maxAttempts: 60, onError: () => setPollTimedOut(true) })

  function recheckQuality (): void {
    setRechecking(true)
    router.post(`/projects/${project.id}/requirements_specification/audit`)
  }

  const artifactVolume = requirementsQuality?.artifact_volume
  const issues = requirementsQuality?.issues
  const risk = requirementsQuality?.risk
  const criticalCount = issues?.critical.length ?? 0
  const volumeMax = (artifactVolume != null) ? Math.max(...VOLUME_ITEMS.map(i => artifactVolume[i.key]), 1) : 1
  const categoryBreakdown = risk?.category_breakdown
  const categoryMax = (categoryBreakdown != null) ? Math.max(...CATEGORY_ITEMS.map(i => categoryBreakdown[i.key]), 1) : 1
  const { label: scoreLevelLabel, color: scoreLevelColor } = RISK_LEVEL_META[risk?.level ?? 'low']

  return (
    <AppLayout project={project} mainClassName='overflow-hidden flex flex-col'>
      <ScreenGuideDialog
        open={guide.open}
        onClose={handleClose}
        title={onboardingContent.title}
        sections={onboardingContent.sections}
      />

      {((props.flash?.notice != null && props.flash?.notice !== '') || (props.flash?.alert != null && props.flash?.alert !== '')) && (
        <div style={{ padding: '12px 20px 0' }}>
          <FlashBanner />
        </div>
      )}
      {pollTimedOut && (
        <div style={{ padding: '12px 20px 0' }}>
          <ErrorBanner message='処理に時間がかかっています。ページを再読み込みしてください。' />
        </div>
      )}

      {/* ステータスバー */}
      {(kgStale || auditInProgress) && (
        <div style={{
          padding: '8px 20px',
          background: 'var(--bp-warning-pale)',
          borderBottom: '1px solid var(--bp-warning-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0
        }}
        >
          {auditInProgress
            ? (
              <span style={{
                fontSize: 13,
                color: 'var(--bp-warning)',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              >
                <span style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: 'var(--bp-warning)',
                  animation: 'srpulse 1.2s ease-in-out infinite'
                }}
                />
                品質チェックを実行中です...
              </span>
              )
            : (
              <>
                <span style={{ fontSize: 13, color: 'var(--bp-warning)', fontWeight: 500 }}>
                  要件定義が修正されました。品質チェックを再依頼してください
                </span>
                <Button
                  variant='outline'
                  className='text-[12px]'
                  style={{ padding: '4px 12px' }}
                  onClick={recheckQuality}
                  disabled={rechecking}
                >
                  {rechecking ? '依頼中...' : '品質チェックを再依頼する'}
                </Button>
              </>
              )}
        </div>
      )}

      {/* コンテンツ */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {requirementsQuality === null
          ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>🔍</div>
              <div style={{
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--bp-ink)',
                marginBottom: 8
              }}
              >品質チェック未実施
              </div>
              <div style={{ fontSize: 13, color: 'var(--bp-ink3)', marginBottom: 20 }}>
                要件定義画面で品質チェックを依頼すると、結果がここに表示されます
              </div>
              <Button asChild>
                <Link href={`/projects/${project.id}/requirements_specification`}>
                  要件定義レビューへ
                </Link>
              </Button>
            </div>
            )
          : (
            <>
              {/* スコアサマリー */}
              <div
                className='bg-(--bp-panel-bg) text-(--bp-panel-ink) rounded-xl' style={{
                  padding: '20px 24px',
                  marginBottom: 24,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 32,
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--bp-panel-ink2)',
                    marginBottom: 8
                  }}
                  >
                    要件品質スコア
                  </div>
                  <ScoreArc score={risk?.safety_score ?? 0} />
                  <div style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                    color: scoreLevelColor,
                    marginTop: 6
                  }}
                  >
                    {scoreLevelLabel}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', color: 'var(--bp-panel-ink2)', marginBottom: 8 }}>
                      成果物の作り込み（{(artifactVolume != null) ? VOLUME_ITEMS.reduce((sum, i) => sum + artifactVolume[i.key], 0) : 0}要素）
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                      {VOLUME_ITEMS.map(item => (
                        <StatBar key={item.key} label={item.label} value={artifactVolume?.[item.key] ?? 0} max={volumeMax} color='var(--bp-accent)' />
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', color: 'var(--bp-panel-ink2)', marginBottom: 8 }}>
                      リスクの内訳（{criticalCount + (issues?.warning.length ?? 0)}件）
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                      {CATEGORY_ITEMS.map(item => (
                        <StatBar key={item.key} label={item.label} value={categoryBreakdown?.[item.key] ?? 0} max={categoryMax} color='var(--bp-critical)' />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <RisksTab
                issues={issues}
                categorySummaries={risk?.category_summaries}
                criticalCount={criticalCount}
                publicFunnelEnabled={publicFunnelEnabled}
                projectId={project.id}
                onOpenHelp={handleOpenHelp}
              />
            </>
            )}
      </div>
    </AppLayout>
  )
}
